import React, { useState, useRef, useEffect } from 'react';
import YouTubeVideoViewer from '@/components/messages/YouTubeVideoViewer';
import SMSForm from '@/components/forms/SMSForm';
import SMSResponseModal from '@/components/SMSResponseModal';
import { X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const SMS_FORM_ENABLED = false; // Set to true to enable SMS form in waiting video

interface WaitingVideoMessageProps {
  videoUrl: string;
  message: string;
  userQuestion?: string;
  aiResponse?: string;
  onSMSModalEngaged?: (engaged: boolean) => void;
}

export const WaitingVideoMessage: React.FC<WaitingVideoMessageProps> = ({ 
  videoUrl, 
  message, 
  userQuestion = '', 
  aiResponse = '',
  onSMSModalEngaged
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();

  // CRITICAL: Hold AI response ONLY when the SMS form is enabled
  // (Otherwise the waiting video would block the assistant response indefinitely)
  useEffect(() => {
    if (!SMS_FORM_ENABLED) return;
    if (!userQuestion) return;

    window.dispatchEvent(
      new CustomEvent('d2c-hold-ai-response', {
        detail: { hold: true },
      }),
    );

    return () => {
      // Release hold when component unmounts
      window.dispatchEvent(
        new CustomEvent('d2c-hold-ai-response', {
          detail: { hold: false },
        }),
      );
    };
  }, [userQuestion]);

  // Detect clicks outside to release hold
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        window.dispatchEvent(new CustomEvent('d2c-hold-ai-response', { 
          detail: { hold: false } 
        }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isDismissed) return null;

  const hasUserQuestion = userQuestion.length > 0;
  const hasAiResponse = aiResponse.length > 0;

  const handleFormSubmit = () => {
    setIsDismissed(true);
    window.dispatchEvent(new CustomEvent('d2c-hold-ai-response', { 
      detail: { hold: false } 
    }));
  };

  const handleSMSButtonClick = () => {
    setShowSMSModal(true);
  };

  const handleSMSModalOpenChange = (open: boolean) => {
    setShowSMSModal(open);
    if (!open) {
      onSMSModalEngaged?.(false);
    }
  };

  const handleUserEngaged = (engaged: boolean) => {
    onSMSModalEngaged?.(engaged);
  };

  const handleWaitingForResponse = () => {
    // Release hold on AI response so it can complete
    window.dispatchEvent(new CustomEvent('d2c-hold-ai-response', { 
      detail: { hold: false } 
    }));
  };

  const smsButtonText = language === 'fr'
    ? 'Obtenez une copie de votre r&eacute;ponse par SMS'
    : 'Get a copy of your response by SMS';

  return (
    <>
      <div className="flex flex-col gap-3 animate-in fade-in-50 duration-500 w-full relative">
        {hasAiResponse && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDismissed(true)}
            className="absolute top-2 right-2 z-10 h-6 w-6 rounded-full bg-background/80 hover:bg-background"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <div className="bg-muted/50 rounded-lg p-4 border border-border w-full">
          <p className="text-sm text-muted-foreground italic">{message}</p>
        </div>
        <YouTubeVideoViewer videoUrl={videoUrl} buttonText="Watch Video" detectVertical={true} />
        
        {/* SMS Response Button */}
        <Button
          onClick={handleSMSButtonClick}
          className="w-full bg-black hover:bg-black/90 text-white py-3 text-sm font-medium"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          <span dangerouslySetInnerHTML={{ __html: smsButtonText }} />
        </Button>
        
        {SMS_FORM_ENABLED && hasUserQuestion && (
          <div ref={formRef}>
            <SMSForm 
              userQuestion={userQuestion} 
              aiResponse={aiResponse}
              onSubmitSuccess={handleFormSubmit}
            />
          </div>
        )}
      </div>
      
      <SMSResponseModal
        open={showSMSModal}
        onOpenChange={handleSMSModalOpenChange}
        onWaitingForResponse={handleWaitingForResponse}
        onUserEngaged={handleUserEngaged}
      />
    </>
  );
};